import { useState, useEffect } from 'react'
import { useTranslation }      from 'react-i18next'
import { useForm }             from 'react-hook-form'
import { listGroups, createGroup, updateGroup, deleteGroup, listUsers } from '@/api/resources'
import {
  Button, PageHeader, EmptyState, Spinner, FIELD_CLS, SearchInput,
} from '@/components/ui/primitives'
import { fetchAllPages } from '@/utils/fetchAll'
import { UsersRound, Plus, X, Trash2, Pencil, Users } from 'lucide-react'

// ── SlideOver ─────────────────────────────────────────────────────────
function SlideOver({ open, title, onClose, children }) {
  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40" onClick={onClose} />
      )}
      <div className={[
        'fixed top-0 right-0 h-full w-full max-w-xl bg-white dark:bg-slate-900 shadow-2xl z-50',
        'flex flex-col transition-transform duration-300 ease-in-out',
        open ? 'translate-x-0' : 'translate-x-full',
      ].join(' ')}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </>
  )
}

// ── GroupForm ─────────────────────────────────────────────────────────
function GroupForm({ initial, students, onSaved, onCancel }) {
  const { t } = useTranslation()
  const { register, handleSubmit, formState: { isSubmitting, errors } } = useForm({
    defaultValues: {
      name: initial?.name || '',
      student_ids: initial?.members?.map(m => m.student.id) || [],
    }
  })

  const [selectedIds, setSelectedIds] = useState(
    initial?.members?.map(m => m.student.id) || []
  )
  const [search, setSearch] = useState('')
  const [saveError, setSaveError] = useState(null)

  const filtered = students.filter(s =>
    !search ||
    (s.full_name + s.username).toLowerCase().includes(search.toLowerCase())
  )

  function toggleStudent(id) {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  async function onSubmit(data) {
    setSaveError(null)
    try {
      const payload = { name: data.name, student_ids: selectedIds }
      if (initial?.id) {
        await updateGroup(initial.id, payload)
      } else {
        await createGroup(payload)
      }
      onSaved()
    } catch (err) {
      setSaveError(err.response?.data ? JSON.stringify(err.response.data) : err.message)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Name */}
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {t('groups.groupName')} <span className="text-red-500">*</span>
        </label>
        <input
          {...register('name', { required: true })}
          placeholder={t('groups.groupName')}
          className={FIELD_CLS}
        />
        {errors.name && <p className="text-xs text-red-500">{t('common.required')}</p>}
      </div>

      {/* Student picker */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {t('groups.students')}
          <span className="ml-2 text-xs text-[var(--coral)] font-normal">{selectedIds.length} {t('groups.selected')}</span>
        </label>

        {/* Selected chips */}
        {selectedIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pb-2">
            {students
              .filter(s => selectedIds.includes(s.id))
              .map(s => (
                <span key={s.id}
                  className="inline-flex items-center gap-1 bg-[var(--coral-tint)] text-[var(--coral)] text-xs px-2 py-1 rounded-full">
                  {s.full_name || s.username}
                  <button type="button" onClick={() => toggleStudent(s.id)}>
                    <X size={11} />
                  </button>
                </span>
              ))}
          </div>
        )}

        {/* Search */}
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('common.search')}
          className={FIELD_CLS}
        />

        {/* List */}
        <div className="border border-gray-200 dark:border-slate-700 rounded-xl max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-800">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-6">{t('common.noData')}</p>
          ) : filtered.map(s => (
            <label key={s.id}
              className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
              <input
                type="checkbox"
                checked={selectedIds.includes(s.id)}
                onChange={() => toggleStudent(s.id)}
                className="w-4 h-4 accent-blue-600 rounded"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                  {s.full_name || s.username}
                </p>
                <p className="text-xs text-gray-400 dark:text-slate-500">@{s.username}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {saveError && (
        <p className="text-xs text-red-600 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
          {saveError}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 dark:border-slate-800">
        <Button variant="secondary" type="button" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" loading={isSubmitting} icon={<Plus size={15} />}>{t('common.save')}</Button>
      </div>
    </form>
  )
}

// ── GroupCard ─────────────────────────────────────────────────────────
function GroupCard({ group, onEdit, onDelete, t }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-5 shadow-sm space-y-3 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <UsersRound size={18} className="text-[var(--coral)]" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{group.name}</h3>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              {group.member_count ?? group.members?.length ?? 0} {t('groups.members')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={() => onEdit(group)}>
            <Pencil size={14} />
          </Button>
          <Button variant="danger" size="sm" onClick={() => onDelete(group.id)}>
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Member avatars */}
      {group.members?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {group.members.slice(0, 8).map(m => (
            <span key={m.id}
              className="inline-flex items-center gap-1 bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 text-xs px-2 py-0.5 rounded-full">
              {m.student.full_name || m.student.username}
            </span>
          ))}
          {group.members.length > 8 && (
            <span className="text-xs text-gray-400 dark:text-slate-500 flex items-center">
              +{group.members.length - 8} {t('groups.more')}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────
export default function GroupsPage() {
  const { t } = useTranslation()
  const [groups,   setGroups]   = useState([])
  const [students, setStudents] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const [editing,   setEditing]   = useState(null)
  const [groupSearch, setGroupSearch] = useState('')

  function fetchGroups() {
    setLoading(true)
    fetchAllPages(listGroups)
      .then(setGroups)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchGroups()
    fetchAllPages(listUsers, { role: 'student' })
      .then(setStudents)
      .catch(() => {})
  }, [])

  function openCreate() {
    setEditing(null)
    setPanelOpen(true)
  }

  function openEdit(group) {
    setEditing(group)
    setPanelOpen(true)
  }

  async function handleDelete(id) {
    if (!window.confirm(t('groups.deleteConfirm'))) return
    await deleteGroup(id)
    fetchGroups()
  }

  function handleSaved() {
    setPanelOpen(false)
    fetchGroups()
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('nav.groups')}
        subtitle={`${groups.length} ${t('common.total').toLowerCase()}`}
        actions={
          <Button icon={<Plus size={15} />} onClick={openCreate}>
            {t('groups.createNew')}
          </Button>
        }
      />

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size="lg" /></div>
      ) : error ? (
        <div className="text-red-600 text-sm p-4">{error}</div>
      ) : (
        <>
          {/* Filter row */}
          <div className="flex items-center gap-3">
            <SearchInput
              value={groupSearch}
              onChange={e => setGroupSearch(e.target.value)}
              placeholder={t('common.search', 'Qidirish...')}
            />
          </div>

          {groups.filter(g => !groupSearch || g.name.toLowerCase().includes(groupSearch.toLowerCase())).length === 0 ? (
            <EmptyState
              icon={<Users size={48} />}
              title={t('common.noData')}
              action={<Button icon={<Plus size={15} />} onClick={openCreate}>{t('groups.createNew')}</Button>}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {groups
                .filter(g => !groupSearch || g.name.toLowerCase().includes(groupSearch.toLowerCase()))
                .map(g => (
                  <GroupCard key={g.id} group={g} t={t} onEdit={openEdit} onDelete={handleDelete} />
                ))}
            </div>
          )}
        </>
      )}

      <SlideOver
        open={panelOpen}
        title={editing ? t('groups.editGroup') : t('groups.createNew')}
        onClose={() => setPanelOpen(false)}
      >
        <GroupForm
          initial={editing}
          students={students}
          onSaved={handleSaved}
          onCancel={() => setPanelOpen(false)}
        />
      </SlideOver>
    </div>
  )
}


